/**
 * PartitionSchemeFormBody — name + strategy + columns + partition rows
 * orchestrator for `partition-table`. Promotes a non-partitioned table to a
 * partitioned table on engines that support runtime promotion.
 *
 * Per-engine surface:
 *   - mysql:  ALTER TABLE … PARTITION BY {RANGE|LIST|HASH|KEY} (cols) …
 *   - oracle: ALTER TABLE … MODIFY PARTITION BY {RANGE|LIST|HASH|REFERENCE|SYSTEM} (cols) …
 *   - mssql / pgsql / sqlite: rejects (form not rendered — caller surfaces
 *     the documented redirect tooltip via `getSetPartitionSchemeUnsupportedTooltip`).
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice E1.
 */
import { useState } from 'react';

import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import type { QualifiedName } from '../../../../sql-grammar/model';
import type {
  PartitionDef,
  PartitioningKind,
  PartitioningSpec,
} from '../../../../sql-resolver/types/ddlEdit';
import {
  commitSetPartitionScheme,
  getSupportedPartitionKinds,
  validateSetPartitionSchemeForEngine,
} from '../../edit/edits/setPartitionScheme';
import type { VqeDdlEditContext } from '../../edit/editContext';
import {
  parsePartitionRowsInput,
  type PartitionRowDraft,
} from './partitionFormHelpers';
import { FormEngineChip } from './FormEngineChip';

const KIND_LABELS: Record<PartitioningKind, string> = {
  range: 'RANGE',
  list: 'LIST',
  hash: 'HASH',
  key: 'KEY (mysql)',
  reference: 'REFERENCE (oracle)',
  system: 'SYSTEM (oracle)',
};

export interface PartitionSchemeFormBodyProps {
  readonly editContext: VqeDdlEditContext;
  readonly testId: string;
  readonly table: QualifiedName | null;
  readonly onCommitted?: () => void;
  readonly onCancel?: () => void;
}

export function PartitionSchemeFormBody(props: PartitionSchemeFormBodyProps) {
  const { editContext, testId, onCommitted, onCancel, table } = props;
  const engine = editContext.service.engineId;
  const supportedKinds = getSupportedPartitionKinds(engine);
  const [kind, setKind] = useState<PartitioningKind>(supportedKinds[0] ?? 'range');
  const [columnsInput, setColumnsInput] = useState('');
  const [rowsInput, setRowsInput] = useState('');
  const [refConstraint, setRefConstraint] = useState('');
  const [hashCount, setHashCount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  function buildSpec(): PartitioningSpec | { error: string } {
    if (kind === 'reference') {
      const name = refConstraint.trim();
      if (name === '') return { error: 'REFERENCE partitioning requires a parent FK constraint name.' };
      return { kind: 'reference', refConstraint: name };
    }
    if (kind === 'system') {
      const partitions = hashCount.trim() === '' ? undefined : Number(hashCount);
      if (partitions !== undefined && (!Number.isFinite(partitions) || partitions < 1)) {
        return { error: 'Partition count must be a positive integer.' };
      }
      return { kind: 'system', partitions };
    }
    const cols = columnsInput.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
    if (kind === 'hash' || kind === 'key') {
      const partitions = hashCount.trim() === '' ? undefined : Number(hashCount);
      if (partitions !== undefined && (!Number.isFinite(partitions) || partitions < 1)) {
        return { error: 'Partition count must be a positive integer.' };
      }
      if (kind === 'hash') return { kind: 'hash', columns: cols, partitions };
      return { kind: 'key', columns: cols, partitions };
    }
    const drafts: PartitionRowDraft[] = parsePartitionRowsInput(rowsInput);
    const partitions: PartitionDef[] = drafts.map((d) => ({
      name: d.name,
      boundary: kind === 'range'
        ? (d.values === 'MAXVALUE' ? { kind: 'maxvalue' } : { kind: 'range-less-than', valuesText: d.values })
        : { kind: 'list-in', valuesText: d.values },
    }));
    return { kind, columns: cols, partitions };
  }

  async function handleSubmit() {
    setError(null);
    if (!table) {
      setError('Could not resolve the target table from the parked statement.');
      return;
    }
    const spec = buildSpec();
    if ('error' in spec) { setError(spec.error); return; }
    const validation = validateSetPartitionSchemeForEngine(engine, { table, spec });
    if (validation !== null) { setError(validation); return; }
    setPending(true);
    try {
      await commitSetPartitionScheme(editContext, { table, spec });
      onCommitted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPending(false);
    }
  }

  const showColumns = kind !== 'reference' && kind !== 'system';
  const showRows = kind === 'range' || kind === 'list';
  const showHashCount = kind === 'hash' || kind === 'key' || kind === 'system';
  const showRefConstraint = kind === 'reference';

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold">Set partitioning</div>
        <FormEngineChip engine={engine} />
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor={`${testId}-kind`} className="text-xs">Strategy</Label>
        <Select value={kind} onValueChange={(v) => setKind(v as PartitioningKind)}>
          <SelectTrigger
            id={`${testId}-kind`}
            data-testid={`${testId}-kind`}
            className="h-8 font-mono text-xs"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {supportedKinds.map((k) => (
              <SelectItem key={k} value={k}>{KIND_LABELS[k]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {showColumns ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${testId}-columns`} className="text-xs">
            Columns / expressions <span className="text-destructive">*</span>
          </Label>
          <Input
            id={`${testId}-columns`}
            data-testid={`${testId}-columns`}
            value={columnsInput}
            onChange={(e) => setColumnsInput(e.target.value)}
            placeholder="created_at"
            className="h-8 font-mono text-xs"
          />
          <span className="text-[10px] text-muted-foreground">Comma-separated.</span>
        </div>
      ) : null}

      {showHashCount ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${testId}-count`} className="text-xs">
            Partition count {kind === 'system' ? '(optional)' : ''}
          </Label>
          <Input
            id={`${testId}-count`}
            data-testid={`${testId}-count`}
            value={hashCount}
            onChange={(e) => setHashCount(e.target.value)}
            placeholder="4"
            className="h-8 font-mono text-xs"
          />
        </div>
      ) : null}

      {showRefConstraint ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${testId}-ref`} className="text-xs">
            FK constraint <span className="text-destructive">*</span>
          </Label>
          <Input
            id={`${testId}-ref`}
            data-testid={`${testId}-ref`}
            value={refConstraint}
            onChange={(e) => setRefConstraint(e.target.value)}
            placeholder="fk_orders_customer"
            className="h-8 font-mono text-xs"
          />
        </div>
      ) : null}

      {showRows ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${testId}-rows`} className="text-xs">
            Partitions
          </Label>
          <textarea
            id={`${testId}-rows`}
            data-testid={`${testId}-rows`}
            value={rowsInput}
            onChange={(e) => setRowsInput(e.target.value)}
            placeholder={'p2024 = 2025\np2025 = 2026\npmax = MAXVALUE'}
            rows={4}
            className="rounded-md border border-input bg-background px-2 py-1 font-mono text-xs"
          />
          <span className="text-[10px] text-muted-foreground">
            One per line: <code>name = values-text</code>. <code>MAXVALUE</code> emits LESS THAN MAXVALUE.
          </span>
        </div>
      ) : null}

      {error ? (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive"
          data-testid={`${testId}-error`}
        >
          {error}
        </div>
      ) : null}

      <div className="sticky bottom-0 -mx-4 -mb-4 flex items-center justify-end gap-2 border-t bg-popover px-4 py-2">
        <Button type="button" variant="ghost" size="sm" onClick={() => onCancel?.()} data-testid={`${testId}-cancel`}>Cancel</Button>
        <Button type="button" size="sm" disabled={pending} onClick={() => void handleSubmit()} data-testid={`${testId}-submit`}>
          {pending ? 'Applying…' : 'Apply'}
        </Button>
      </div>
    </div>
  );
}
